import {Component, Input, OnInit} from "@angular/core";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {take} from "rxjs/operators";
import {FieldSuggestionOption} from "../../../shared/models/fieldSuggestion";
import {FieldSuggestionService} from "../../../shared/services/field-suggestion/field-suggestion.service";

// Reusable single-choice select fed by an admin-curated suggestion list
// (FieldSuggestion). An option flagged is_free_text (« Autre ») reveals a free
// text input; the stored value is then the typed text. Reused on signup and the
// attendance-confirmation modal (and meant for card 4).
@Component({
    "selector": "app-suggestion-select",
    "templateUrl": "./suggestion-select.component.html",
    "providers": [
        {
            "provide": NG_VALUE_ACCESSOR,
            "multi": true,
            "useExisting": SuggestionSelectComponent
        }
    ]
})
export class SuggestionSelectComponent implements ControlValueAccessor, OnInit {
    @Input() category: string;

    @Input() inputId = "";

    options: Array<FieldSuggestionOption> = [];

    // The currently selected option's value (the <select> model)
    selectedValue = "";

    freeText = "";

    disabled = false;

    touched = false;

    // Value received before the options are loaded, reconciled in ngOnInit
    private pendingValue: string = null;

    onChange = (value) => {};

    onTouched = () => {};

    constructor (private fieldSuggestionService: FieldSuggestionService) {}

    ngOnInit () {
        this.fieldSuggestionService.getOptions(this.category).
            pipe(take(1)).
            subscribe((options) => {
                this.options = options;
                this.reconcile();
            });
    }

    get selectedOption (): FieldSuggestionOption {
        return this.options.find((option) => option.value === this.selectedValue) || null;
    }

    get isFreeText (): boolean {
        const option = this.selectedOption;
        return option != null && option.is_free_text;
    }

    // Match the model value against the loaded options: a known fixed option is
    // selected directly; anything else is treated as the free-text value.
    private reconcile () {
        if (!this.pendingValue) {
            return;
        }

        const fixedMatch = this.options.find(
            (option) => !option.is_free_text && option.value === this.pendingValue
        );

        if (fixedMatch) {
            this.selectedValue = fixedMatch.value;
            this.freeText = "";
        } else {
            const freeOption = this.options.find((option) => option.is_free_text);
            if (freeOption) {
                this.selectedValue = freeOption.value;
                this.freeText = this.pendingValue;
            }
        }

        this.pendingValue = null;
    }

    onSelectChange (value: string) {
        this.markAsTouched();
        if (this.disabled) {
            return;
        }
        this.selectedValue = value;
        this.freeText = "";
        this.emit();
    }

    onFreeTextChange (text: string) {
        this.markAsTouched();
        if (this.disabled) {
            return;
        }
        this.freeText = text;
        this.emit();
    }

    private emit () {
        const option = this.selectedOption;
        if (option == null) {
            this.onChange("");
        } else if (option.is_free_text) {
            this.onChange(this.freeText);
        } else {
            this.onChange(option.value);
        }
    }

    writeValue (value: string) {
        this.pendingValue = value;
        this.selectedValue = "";
        this.freeText = "";
        if (this.options.length) {
            this.reconcile();
        }
    }

    registerOnChange (onChange: any) {
        this.onChange = onChange;
    }

    registerOnTouched (onTouched: any) {
        this.onTouched = onTouched;
    }

    markAsTouched () {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }

    setDisabledState (disabled: boolean) {
        this.disabled = disabled;
    }
}
